import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Spin, Descriptions, List, Avatar, Input, Checkbox, Form, Tag } from 'antd';
import { ArrowLeftOutlined, UserOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ErrorResult } from '../../components/common/ErrorResult';
import { useTicketDetail, useReplyToTicket } from '../../hooks/useSupport';
import { TICKET_STATUS_COLORS, TICKET_PRIORITY_COLORS } from '../../lib/constants';
import dayjs from 'dayjs';

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading, isError, refetch } = useTicketDetail(id!);
  const reply = useReplyToTicket(id!);
  const [form] = Form.useForm();

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (isError || !ticket) return <ErrorResult onRetry={refetch} />;

  const handleReply = (values: { message: string; is_internal: boolean }) => {
    reply.mutate({ message: values.message, is_internal: values.is_internal || false });
    form.resetFields();
  };

  return (
    <>
      <PageHeader title={`${ticket.ticket_number}: ${ticket.subject}`} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/support/tickets')}>Back</Button>} />
      <Card>
        <Descriptions column={3}>
          <Descriptions.Item label="Status"><StatusBadge status={ticket.status} colorMap={TICKET_STATUS_COLORS} /></Descriptions.Item>
          <Descriptions.Item label="Priority"><StatusBadge status={ticket.priority} colorMap={TICKET_PRIORITY_COLORS} /></Descriptions.Item>
          <Descriptions.Item label="Category">{ticket.category || '-'}</Descriptions.Item>
          <Descriptions.Item label="Contact">{ticket.contact_email || ticket.user_email || '-'}</Descriptions.Item>
          <Descriptions.Item label="Assignee">{ticket.assigned_to_name || 'Unassigned'}</Descriptions.Item>
          <Descriptions.Item label="Created">{dayjs(ticket.created_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>
        </Descriptions>
        <p style={{ marginTop: 16 }}>{ticket.description}</p>
      </Card>
      <Card title="Messages" style={{ marginTop: 16 }}>
        <List
          dataSource={ticket.messages}
          renderItem={(msg) => (
            <List.Item style={{ background: msg.is_internal ? '#fffbe6' : undefined, padding: '12px 16px', borderRadius: 6, marginBottom: 8 }}>
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<>{msg.sender_name} <small style={{ color: '#8c8c8c' }}>{dayjs(msg.created_at).format('MMM D HH:mm')}</small> {msg.is_internal && <Tag color="orange">Internal</Tag>}</>}
                description={msg.message}
              />
            </List.Item>
          )}
        />
        <Form form={form} onFinish={handleReply} style={{ marginTop: 16 }}>
          <Form.Item name="message" rules={[{ required: true }]}>
            <Input.TextArea rows={3} placeholder="Type your reply..." />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={reply.isPending}>Send Reply</Button>
            <Form.Item name="is_internal" valuePropName="checked" noStyle><Checkbox>Internal Note</Checkbox></Form.Item>
          </Space>
        </Form>
      </Card>
    </>
  );
}
